import Link from 'next/link';
import { trackEvent } from '../../../util/analytics';

/**
 * A "Book a Demo" link that reports itself to GA4.
 *
 * Every marketing demo CTA points at /demo, so GA4's `book_demo_click` key
 * event — really a page_view of /demo synthesised in the admin UI — cannot
 * tell you WHICH button earned the visit, and counts arrivals from any route.
 * `demo_cta_click` + `cta_location` is what makes that answerable.
 *
 * This exists as a component rather than an onClick pasted at each call site
 * because there are ten of them: `util/analytics.js` already insists that no
 * raw `gtag('event', …)` calls be scattered through components, and ten
 * hand-copied handlers reproduce exactly that problem one layer up. Wrapping
 * it means a new marketing section gets tracking by reaching for the normal
 * link, without anyone having to remember the parameter shape.
 *
 * Presentation is entirely the caller's: `className` and any other props pass
 * straight through to next/link, so swapping a plain <Link> for this one is a
 * visual no-op.
 */
/**
 * The `demo_cta_click` payload, in one place.
 *
 * Exported because the navbar cannot use the component below: its CTAs render
 * through navConfig data via MotionLink (framer-motion) and a mobile <button>,
 * neither of which can be swapped for a plain link without losing the
 * animation and the drawer-close behaviour. They call this instead, so the
 * parameter shape has a single definition and the two surfaces cannot drift
 * into reporting different keys for the same event.
 */
export function trackDemoCtaClick(ctaLocation) {
  trackEvent('demo_cta_click', {
    event_source: 'site',
    cta_location: ctaLocation,
    page_path: typeof window !== 'undefined' ? window.location.pathname : '',
  });
}

export function DemoCtaLink({
  ctaLocation,
  children,
  href = '/demo',
  onClick,
  ...linkProps
}) {
  // `onClick` is pulled out of the spread deliberately. RoiResults already
  // passes its own handler, and with a bare {...linkProps} spread the caller's
  // would replace this one — killing tracking at precisely the call sites that
  // care enough about the click to have written a handler. Track first, then
  // hand the event on.
  const handleClick = (event) => {
    trackDemoCtaClick(ctaLocation);
    if (onClick) onClick(event);
  };

  return (
    <Link href={href} onClick={handleClick} {...linkProps}>
      {children}
    </Link>
  );
}
